import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Minus, Plus, X } from "lucide-react";
import {
  counterPayout,
  formatOutcome,
  getOfferedOdds,
  getRemainingCounterCapacity,
  getUserStake,
  getMinCounterStake,
  P2P_TERMS,
} from "../../utils/p2pHelpers";
import { P2P_PLATFORM_FEE_PERCENT, p2pFeeFromGrossAndStake } from "../../config/fees";

const JoinP2PModal = ({
  open,
  onClose,
  onConfirm,
  bet,
  loading = false,
  mode = "join",
  userId = null,
}) => {
  const [agreed, setAgreed] = useState(false);
  const [stakeInput, setStakeInput] = useState("");

  const offeredOdds = bet ? getOfferedOdds(bet) : 2;
  const minStake = getMinCounterStake(bet);
  const maxStake = bet ? getRemainingCounterCapacity(bet) : 0;
  const existingStake = bet && userId ? getUserStake(bet, userId) : 0;
  const isAddStake = existingStake > 0 || mode === "add-stake";
  const capacityOpen = maxStake >= minStake;
  const isInvite = mode === "invite";

  useEffect(() => {
    if (!open) return;
    setAgreed(false);
    setStakeInput(String(minStake));
  }, [open, bet?._id, minStake]);

  const stake = useMemo(() => {
    const n = parseFloat(String(stakeInput).replace(/[^0-9.]/g, ""));
    if (!Number.isFinite(n) || n <= 0) return 0;
    const floored = Math.max(minStake, n);
    return maxStake > 0 ? Math.min(floored, maxStake) : floored;
  }, [stakeInput, minStake, maxStake]);

  if (!open || !bet) return null;

  const fixture = bet.fixture || {};
  const home = fixture.teams?.home || fixture.homeTeam || {};
  const away = fixture.teams?.away || fixture.awayTeam || {};
  const outcome = formatOutcome(bet.agreedOdds, fixture);
  const potentialWin = counterPayout(stake, offeredOdds);
  const { fee: winFee, net: netWin } = p2pFeeFromGrossAndStake(potentialWin, stake);
  const disabled = !agreed || loading || !capacityOpen || stake < minStake;

  const adjustStake = (delta) => {
    setStakeInput((prev) => {
      const base = parseFloat(String(prev).replace(/[^0-9.]/g, "")) || minStake;
      const next = Math.max(minStake, base + delta);
      const capped = maxStake > 0 ? Math.min(next, maxStake) : next;
      return String(Math.round(capped));
    });
  };

  const handleConfirm = () => {
    if (disabled) return;
    onConfirm?.({ stake });
  };

  const title = isAddStake
    ? "Add stake"
    : isInvite
      ? "Accept invite"
      : P2P_TERMS.betAgainst;
  const confirmLabel = isAddStake
    ? `Add Cash ${stake.toLocaleString()}`
    : isInvite
      ? P2P_TERMS.acceptAndAgainst
      : P2P_TERMS.confirmAgainst;

  return createPortal(
    <div className="p2p-modal-root" role="dialog" aria-modal="true">
      <button type="button" className="p2p-modal-backdrop" aria-label="Close" onClick={onClose} />
      <div className="p2p-modal-sheet">
        <header className="p2p-modal-head">
          <div>
            <p className="p2p-modal-eyebrow">{title}</p>
            <h2 className="p2p-modal-title">{title}</h2>
          </div>
          <button type="button" className="p2p-modal-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>

        <div className="p2p-modal-match">
          <span className="p2p-modal-team">{home.name || "Home"}</span>
          <span className="p2p-modal-vs">vs</span>
          <span className="p2p-modal-team">{away.name || "Away"}</span>
        </div>

        <div className="p2p-modal-rows">
          <div className="p2p-modal-row">
            <span>{P2P_TERMS.creatorBacking}</span>
            <strong>{outcome}</strong>
          </div>
          <div className="p2p-modal-row">
            <span>Offered odds</span>
            <strong>{offeredOdds.toFixed(2)}</strong>
          </div>
          <div className="p2p-modal-row">
            <span>Capacity left</span>
            <strong>
              {capacityOpen
                ? `Cash ${maxStake.toLocaleString()}`
                : "Full"}
            </strong>
          </div>
        </div>

        {isAddStake ? (
          <p className="p2p-modal-your-spots">
            Your current stake: Cash {existingStake.toLocaleString()}
          </p>
        ) : null}

        {capacityOpen ? (
          <div className="p2p-modal-spots">
            <p className="p2p-modal-spots-label">
              Your stake (min stake Cash {minStake.toLocaleString()})
            </p>
            <div className="p2p-modal-spots-stepper">
              <button
                type="button"
                className="p2p-modal-spot-btn"
                onClick={() => adjustStake(-500)}
                disabled={stake <= minStake || loading}
                aria-label="Decrease stake"
              >
                <Minus size={16} />
              </button>
              <div className="p2p-modal-spot-value">
                <input
                  type="number"
                  inputMode="numeric"
                  min={minStake}
                  max={maxStake > 0 ? maxStake : undefined}
                  value={stakeInput}
                  onChange={(e) => setStakeInput(e.target.value.replace(/[^0-9.]/g, ""))}
                  className="p2p-modal-stake-input"
                  aria-label="Stake amount"
                />
                <span>max Cash {maxStake.toLocaleString()}</span>
              </div>
              <button
                type="button"
                className="p2p-modal-spot-btn"
                onClick={() => adjustStake(500)}
                disabled={(maxStake > 0 && stake >= maxStake) || loading}
                aria-label="Increase stake"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>
        ) : null}

        <div className="p2p-modal-rows p2p-modal-rows-totals">
          <div className="p2p-modal-row">
            <span>Your stake</span>
            <strong>{stake.toLocaleString()} Cash</strong>
          </div>
          <div className="p2p-modal-row">
            <span>Potential win</span>
            <strong className="p2p-modal-win">{potentialWin.toLocaleString()} Cash</strong>
          </div>
          <div className="p2p-modal-row">
            <span>Fee ({P2P_PLATFORM_FEE_PERCENT}% of profit)</span>
            <strong>{Math.round(winFee).toLocaleString()} Cash</strong>
          </div>
          <div className="p2p-modal-row">
            <span>You receive if you win</span>
            <strong className="p2p-modal-win">{Math.round(netWin).toLocaleString()} Cash</strong>
          </div>
        </div>

        <label className={`p2p-modal-consent ${agreed ? "p2p-modal-consent-active" : ""}`}>
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
          />
          <span>
            I understand <strong>{stake.toLocaleString()} Cash</strong> will be locked once
            confirmed. If I win, a {P2P_PLATFORM_FEE_PERCENT}% fee applies to my profit.
          </span>
        </label>

        <footer className="p2p-modal-foot">
          <button type="button" className="p2p-modal-btn p2p-modal-btn-ghost" onClick={onClose}>
            Back
          </button>
          <button
            type="button"
            className="p2p-modal-btn p2p-modal-btn-primary"
            disabled={disabled}
            onClick={handleConfirm}
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : confirmLabel}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default JoinP2PModal;
